import { useSyncExternalStore } from "react";

export interface AdConsent { ads: boolean; networkIds: string[]; tcf?: string; gpp?: string; }
export interface AdsConfig { enabled: boolean; appId: "focalfolio"; managerUrl: string; frameOrigin: string; minViewportWidth: number; }
export interface AdController { setConsent: (consent: AdConsent) => void; destroy: () => void; }
export interface AdsSDK {
  version: string;
  mount: (options: { element: HTMLElement; appId: string; managerUrl: string; minViewportWidth: number; cssUrl: string; privacyUrl: string; consent: AdConsent }) => AdController;
  setConsent: (consent: AdConsent) => void;
}
declare const __FOCALFOLIO_ADS__: AdsConfig;
declare global {
  interface Window {
    PicPlayerAds?: AdsSDK;
    FocalFolioAds: { setConsent: (consent: AdConsent) => void; withdrawConsent: () => void; openPreferences: () => void };
  }
}
// All values here are PUBLIC build-time configuration, never manager credentials.
export const ADS_CONFIG = __FOCALFOLIO_ADS__;
const preferenceKey = "focalfolio-ad-sidebar";
const changeEvent = "focalfolio:ad-sidebar-change";
let volatilePreference: boolean | undefined;
let consent: AdConsent = { ads: false, networkIds: [] };
let sdkPromise: Promise<AdsSDK> | undefined;

function preference() {
  if (volatilePreference !== undefined) return volatilePreference;
  try { return localStorage.getItem(preferenceKey) !== "hidden"; } catch { return true; }
}
function subscribe(callback: () => void) {
  const storage = (event: StorageEvent) => { if (event.key === preferenceKey || event.key === null) callback(); };
  window.addEventListener(changeEvent, callback);
  window.addEventListener("storage", storage);
  return () => { window.removeEventListener(changeEvent, callback); window.removeEventListener("storage", storage); };
}
export function useAdSidebarEnabled() { return useSyncExternalStore(subscribe, preference, () => true) && ADS_CONFIG.enabled; }
export function setAdSidebarEnabled(enabled: boolean) {
  try { localStorage.setItem(preferenceKey, enabled ? "visible" : "hidden"); volatilePreference = undefined; }
  catch { volatilePreference = enabled; /* Honor the choice even when only storage writes are blocked. */ }
  if (!enabled) withdrawAdConsent();
  window.dispatchEvent(new Event(changeEvent));
}
export function currentAdConsent() { return consent; }
export function setAdConsent(next: AdConsent) {
  // A real CMP supplies network-specific consent. A sidebar toggle is NOT consent.
  const restricted = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true || navigator.doNotTrack === "1";
  consent = {
    ads: !restricted && next?.ads === true,
    networkIds: Array.isArray(next?.networkIds) ? next.networkIds.filter(id => typeof id === "string" && /^[a-z0-9][a-z0-9-]{1,63}$/.test(id)).slice(0, 100) : [],
    tcf: typeof next?.tcf === "string" ? next.tcf.slice(0, 4096) : "",
    gpp: typeof next?.gpp === "string" ? next.gpp.slice(0, 4096) : ""
  };
  window.PicPlayerAds?.setConsent(consent);
}
export function withdrawAdConsent() { setAdConsent({ ads: false, networkIds: [] }); }
export function openAdPreferences() { document.dispatchEvent(new CustomEvent("focalfolio:ad-preferences", { cancelable: true })); }
export function localPublicURL(path: string) { return new URL(`${import.meta.env.BASE_URL}${path}`, location.href).href; }

/** Load the exact vendored PicPlayer SDK from FocalFolio's own PWA origin. */
export function loadAdsSDK(): Promise<AdsSDK> {
  if (window.PicPlayerAds) return Promise.resolve(window.PicPlayerAds);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<AdsSDK>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = localPublicURL("ads/rail.js");
    script.async = true;
    script.referrerPolicy = "no-referrer";
    const timer = setTimeout(() => fail(), 10_000);
    const fail = () => { clearTimeout(timer); script.remove(); sdkPromise = undefined; reject(new Error("The local ad sidebar could not be loaded.")); };
    script.onerror = fail;
    script.onload = () => { clearTimeout(timer); if (window.PicPlayerAds) resolve(window.PicPlayerAds); else fail(); };
    document.head.append(script);
  });
  return sdkPromise;
}

window.FocalFolioAds = { setConsent: setAdConsent, withdrawConsent: withdrawAdConsent, openPreferences: openAdPreferences };
